// see `docs/features.md` for more human-friendly features list

export const DEBUG_FEATURE = 'DEBUG_FEATURE' as const;

export const EDITOR_FEATURE = {
  accessibility: 'accessibility',
  addPanel: 'addPanel',
  addSectionPanel: 'addSectionPanel',
  appMarket: 'appMarket',
  privateApps: 'privateApps',
  comments: 'comments',
  component: 'component',
  contentManager: 'contentManager',
  duplicateApp: 'duplicateApp',
  editorTour: 'editorTour',
  layersPanel: 'layersPanel',
  menuManager: 'menuManager',
  pagesPanel: 'pagesPanel',
  appPage: 'appPage',
  renameApp: 'renameApp',
  rolesAndPermissions: 'rolesAndPermissions',
  search: 'search',
  siteCheckPanel: 'siteCheckPanel',
  themePanel: 'themePanel',
  upgrade: 'upgrade',
  viewport: 'viewport',
  inspectorPanel: 'inspectorPanel',
  contentEditingMode: 'contentEditingMode',
  widgetPanel: 'widgetPanel',
  dashboardPanel: 'dashboardPanel',
  communityPanel: 'communityPanel',
  educationPanel: 'educationPanel',
  photoStudio: 'photoStudio',
  multilingual: 'multilingual',
  addPlugin: 'addPlugin',
  myBusiness: 'myBusiness',
  dynamicAppPage: 'dynamicAppPage',
  addPage: 'addPage',
  addDynamicPage: 'addDynamicPage',
  customPanel: 'customPanel',
  seoPanel: 'seoPanel',
  socialSharePanel: 'socialSharePanel',
  [DEBUG_FEATURE]: DEBUG_FEATURE,
} as const;

export const APPLICATION_FEATURE = {
  // TODO
} as const;

export const FEATURE = {
  ...EDITOR_FEATURE,
  ...APPLICATION_FEATURE,
} as const;

export const FEATURE_PARAMS = {
  [FEATURE.accessibility]: ['pageId'],
  [FEATURE.addPanel]: [
    'appDefinitionId',
    'componentType',
    'widgetId',
    'categoryId',
    'groupId',
    'subgroupId',
  ],
  [FEATURE.addSectionPanel]: [],
  [FEATURE.appMarket]: ['appDefinitionId'],
  [FEATURE.privateApps]: ['appDefinitionId', 'version'],
  [FEATURE.comments]: ['pageId', 'commentId'],
  [FEATURE.component]: ['id', 'panelName'],
  [FEATURE.contentManager]: ['tab'],
  [FEATURE.duplicateApp]: [],
  [FEATURE.editorTour]: ['tourName'],
  [FEATURE.layersPanel]: [],
  [FEATURE.menuManager]: [],
  [FEATURE.pagesPanel]: ['appDefinitionId'],
  [FEATURE.renameApp]: [],
  [FEATURE.rolesAndPermissions]: [],
  [FEATURE.search]: [],
  [FEATURE.siteCheckPanel]: [],
  [FEATURE.themePanel]: [],
  [FEATURE.upgrade]: [],
  [FEATURE.viewport]: ['size'],
  [FEATURE.inspectorPanel]: ['category'],
  [FEATURE.contentEditingMode]: ['contentEditingMode'],
  [FEATURE.widgetPanel]: ['devCenterComponentId'],
  [FEATURE.dashboardPanel]: ['devCenterComponentId'],
  [FEATURE.communityPanel]: [],
  [FEATURE.educationPanel]: [],
  [FEATURE.photoStudio]: ['mode', 'file'],
  [FEATURE.multilingual]: ['languageCode'],
  [FEATURE.addPlugin]: [],
  [FEATURE.myBusiness]: ['appDefinitionId'],
  [FEATURE.appPage]: ['appDefinitionId', 'extensionId'],
  [FEATURE.dynamicAppPage]: ['appDefinitionId', 'extensionId', 'slug'],
  [FEATURE.addPage]: ['categoryId'],
  [FEATURE.addDynamicPage]: ['collectionId', 'pageType', 'origin'],
  [FEATURE.customPanel]: ['devCenterComponentId'],
  [FEATURE.seoPanel]: ['pageId'],
  [FEATURE.socialSharePanel]: ['pageId'],
  [DEBUG_FEATURE]: ['arg0', 'arg1', 'arg2'],
} as const;

export const FEATURE_REQUIRED_PARAMS = {
  [FEATURE.viewport]: ['size'],
  [FEATURE.component]: ['id'],
  [FEATURE.privateApps]: ['appDefinitionId'],
  [FEATURE.multilingual]: ['languageCode'],
  [FEATURE.appPage]: ['appDefinitionId', 'extensionId'],
  [FEATURE.dynamicAppPage]: ['appDefinitionId', 'extensionId', 'slug'],
} as const;

export const FEATURE_PARAM_VALUES = {
  [FEATURE.viewport]: {
    size: ['phone', 'tablet', 'desktop'],
  },
  [FEATURE.contentManager]: {
    tab: ['collections', 'elements', 'learn'],
  },
  [FEATURE.component]: {
    // based on https://github.com/wix-private/santa-editor/blob/406be79a31216370ffb07184fb3e40dc56bd757a/santa-editor/packages/constants/base/baseConstants.ts#L452-L470
    panelName: [
      'animation',
      'apply',
      'background',
      'behaviors',
      // 'customizeDesign', // 'design' might be enough
      'design',
      'effects',
      'filters',
      'layout',
      'link',
      'manage',
      'mask',
      // 'mobileBackgroundSettings', // 'background' might be enough
      'settings',
      'smartWriter',
      'stretching',
      'stylable',
    ],
  },
} as const;

// exported to keep backwards compatability. remove if/when bumping major version
export const VIEWPORT_SIZE_VALUES = FEATURE_PARAM_VALUES[FEATURE.viewport].size;
export const CONTENT_MANAGER_TABS =
  FEATURE_PARAM_VALUES[FEATURE.contentManager].tab;

export type ParamValues = typeof FEATURE_PARAM_VALUES;
export type FeatureParams = typeof FEATURE_PARAMS;
export type FeatureRequiredParams = typeof FEATURE_REQUIRED_PARAMS;

function featureHasRequiredParams(
  type: FeatureType,
): type is keyof FeatureRequiredParams {
  return type in FEATURE_REQUIRED_PARAMS;
}

function featureHasLimitedParams(type: FeatureType): type is keyof ParamValues {
  return type in FEATURE_PARAM_VALUES;
}

export function areParamsValid<T extends FeatureType>(
  type: T,
  params: string[],
): params is Feature<T>['params'] {
  if (featureHasRequiredParams(type)) {
    const requiredParams: readonly string[] = FEATURE_REQUIRED_PARAMS[type];

    if (params.length < requiredParams.length) {
      return false;
    }

    if (requiredParams.some((name, i) => !params[i])) {
      return false;
    }
  }

  if (featureHasLimitedParams(type)) {
    const paramsWithLimitedValues: Record<string, readonly string[]> =
      FEATURE_PARAM_VALUES[type];
    const paramNames = FEATURE_PARAMS[type];

    const hasInvalidValue = params.some((value, i) => {
      const name = paramNames[i];

      if (name && name in paramsWithLimitedValues) {
        const possibleValues = paramsWithLimitedValues[name];

        if (possibleValues.indexOf(value) === -1) {
          return true;
        }
      }

      return false;
    });

    if (hasInvalidValue) {
      return false;
    }
  }

  return true;
}

// name of T's Nth param, for example:
// - `FeatureParam<"appMarket", 0> === "appDefinitionId"`
// - `FeatureParam<"addPanel", 1> === "componentType"`
// - `FeatureParam<"viewport", 0> === "size"`
export type FeatureParam<
  T extends FeatureType,
  N extends number,
> = N extends keyof FeatureParams[T]
  ? FeatureParams[T][N] extends undefined
    ? never
    : FeatureParams[T][N]
  : never;

// type of T's Nth param, for example:
// - `ParamType<"addPanel", 1> === string`
// - `ParamType<"viewport", 0> === "phone" | "tablet" | "desktop"`
export type ParamType<
  T extends FeatureType,
  N extends number,
> = (T extends keyof ParamValues
  ? FeatureParam<T, N> extends keyof ParamValues[T]
    ? ParamValues[T][FeatureParam<T, N>]
    : string[]
  : string[])[number]; // I have no idea why `(ParamValues[T][FeatureParam<T, N>])[number]` doesn't work, so i worked around it

type NeverIfSmallerThanRequired<
  T extends FeatureType,
  U,
> = T extends keyof FeatureRequiredParams
  ? FeatureRequiredParams[T] extends readonly [string]
    ? U extends readonly []
      ? never
      : U
    : FeatureRequiredParams[T] extends readonly [string, string]
    ? U extends readonly [] | readonly [string]
      ? never
      : U
    : never
  : U;

export type Params<T extends FeatureType = FeatureType> =
  FeatureParams[T] extends readonly [string]
    ? NeverIfSmallerThanRequired<T, [] | [ParamType<T, 0>]>
    : FeatureParams[T] extends readonly [string, string]
    ? NeverIfSmallerThanRequired<
        T,
        [] | [ParamType<T, 0>] | [ParamType<T, 0>, ParamType<T, 1>]
      >
    : FeatureParams[T] extends readonly [string, string, string]
    ? NeverIfSmallerThanRequired<
        T,
        | []
        | [ParamType<T, 0>]
        | [ParamType<T, 0>, ParamType<T, 1>]
        | [ParamType<T, 0>, ParamType<T, 1>, ParamType<T, 2>]
      >
    : NeverIfSmallerThanRequired<T, string[]>;

export type FeatureType = keyof typeof FEATURE;
export type Feature<T extends FeatureType = FeatureType> = {
  type: T;
  params: Params<T>;
};

export type EditorFeatureType = keyof typeof EDITOR_FEATURE;
export type EditorFeature = Feature<EditorFeatureType>;

export type ApplicationFeatureType = keyof typeof APPLICATION_FEATURE;
export type ApplicationFeature = Feature<ApplicationFeatureType>;
